import { ExternalLink, XIcon, ZoomIn, ZoomOut } from 'lucide-react';
import { type MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const CENTER_FOCUS_POINT = { x: 0.5, y: 0.5 };

const toolbarButtonClass = 'text-white hover:bg-white/15 hover:text-white';

interface PhotoPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photoUrl: string;
  title: string;
}

export function PhotoPreviewDialog({
  open,
  onOpenChange,
  photoUrl,
  title,
}: PhotoPreviewDialogProps) {
  const [isZoomed, setIsZoomed] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const focusPointRef = useRef(CENTER_FOCUS_POINT);

  useLayoutEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!isZoomed || !scrollArea) return;
    const { x, y } = focusPointRef.current;
    scrollArea.scrollLeft = x * scrollArea.scrollWidth - scrollArea.clientWidth / 2;
    scrollArea.scrollTop = y * scrollArea.scrollHeight - scrollArea.clientHeight / 2;
  }, [isZoomed]);

  const zoomInAt = (focusPoint: { x: number; y: number }) => {
    focusPointRef.current = focusPoint;
    setIsZoomed(true);
  };

  const handleImageClick = (event: MouseEvent<HTMLImageElement>) => {
    if (isZoomed) {
      setIsZoomed(false);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    zoomInAt({
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setIsZoomed(false);
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        overlayClassName="bg-black/70"
        className="flex h-[90dvh] flex-col gap-0 overflow-hidden bg-neutral-950 p-0 text-white ring-0 sm:max-w-6xl"
      >
        <div className="flex shrink-0 items-center justify-between gap-2 py-2 pr-2 pl-4">
          <DialogTitle className="truncate text-sm font-medium text-white">{title}</DialogTitle>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              className={toolbarButtonClass}
              aria-label={isZoomed ? 'Zoom out' : 'Zoom in'}
              onClick={() => (isZoomed ? setIsZoomed(false) : zoomInAt(CENTER_FOCUS_POINT))}
            >
              {isZoomed ? <ZoomOut /> : <ZoomIn />}
            </Button>
            <a
              href={photoUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Open original"
              className={cn(
                buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
                toolbarButtonClass,
              )}
            >
              <ExternalLink />
            </a>
            <DialogClose
              render={<Button variant="ghost" size="icon-sm" className={toolbarButtonClass} />}
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </div>

        <div
          ref={scrollAreaRef}
          className={cn(
            'min-h-0 flex-1',
            isZoomed ? 'overflow-auto' : 'flex items-center justify-center overflow-hidden p-2',
          )}
        >
          <img
            src={photoUrl}
            alt={title}
            onClick={handleImageClick}
            className={cn(
              isZoomed
                ? 'w-[200%] max-w-none cursor-zoom-out'
                : 'max-h-full max-w-full cursor-zoom-in object-contain',
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
